import ForumIcon from '@mui/icons-material/Forum';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { errorMessage } from 'constants/CommonConstants';
import { EMAIL_REGEX, PHONE_REGEX } from 'constants/Regex';
import { formPatternRules, formRequiredRules } from 'helper';
import useQueryParams from 'hooks/UseQueryParams';
import React, { useEffect, useImperativeHandle, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CheckboxInput, CommonInput } from 'sharedComponents/inputGroup';
import { CommonPhoneInput } from 'sharedComponents/inputGroup/CommonPhoneInput';
import { postTradeInLeadCreation } from 'store/TradeInState';
import { RootState } from 'store/store';
import {
  BLACK_BOOK_QUESTIONS_V2,
  transformMultiChoiceData,
  transformSingleChoiceCustomData,
  transformSingleChoiceData,
  VEHICLE_CONDITIONS_PAGE_2,
} from '../TradeInConstant';
import { transformQuestions } from '../TradeInUtils';
import { postPublicLeadCreationOld } from 'store/CreditScoreState';
import { AutoCompleteAddress } from 'sharedComponents/inputGroup/AutoCompleteAddress';
import Logger from 'utils/logger';

const PersonalInfo: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();

  const version = queryParams.get('version');

  const hasCalledNextStep = React.useRef(false);

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();
  const { authenticateClientData } = useSelector((state: RootState) => state.auth);
  const { vehicleOptions } = useSelector((state: RootState) => state.tradeInStore);
  const { offerValue } = useSelector((state: RootState) => state.commonModuleStore);

  const leadSourceId = authenticateClientData?.data?.widgetData?.leadSourceId;
  const year = queryParams.get('year');
  const make = queryParams.get('make');
  const model = queryParams.get('model');
  const trim = queryParams.get('trim');
  const heading = queryParams.get('heading');
  const uvc = queryParams.get('uvc');
  const dealershipName = authenticateClientData.data?.widgetData?.dealershipName;
  const { control, setValue, watch, trigger, getValues } = useFormContext();

  const headingWatcher = watch('heading');

  useEffect(() => {
    setTimeout(() => {
      const vehicleSearch = getValues('vehicleSearch') || {};
      setValue('year', year || vehicleSearch.year || '');
      setValue('make', make || vehicleSearch.make || '');
      setValue('model', model || vehicleSearch.model || '');
      setValue('trim', trim || vehicleSearch.trim || '');
      setValue('heading', heading || vehicleSearch.heading || '');
    });
  }, [year, make, model, trim, heading, setValue, getValues]);

  const vehicleOptionsArray = useMemo(() => {
    return [
      {
        id: 1,
        label: t('stepperFormTradeIn.que1'),
        labelTxt: 'Select any options your vehicle has',
        options: vehicleOptions?.map((option: { name: string; _id: string }, index: number) => ({
          value: `Option_${index + 1}`,
          label: option.name,
        })),
      },
    ];
  }, [vehicleOptions]);

  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const {
      firstName,
      lastName,
      phone,
      email,
      mileage,
      year,
      model,
      make,
      trim,
      province,
      exteriorColor,
      interiorColor,
      tradein_address_country,
      tradein_address_postal,
      tradein_address_lookupStreetName,
      tradein_address_lookupStreetNumber,
      tradein_address_city,
      vehicleSearch,
    } = getValues();

    // Extract custom-key-* fields from widgetData and add them to updatedData
    const widgetData = authenticateClientData?.data?.widgetData || {};
    const customFields = Object.keys(widgetData)
      .filter((key) => key?.startsWith('custom-key-'))
      .reduce(
        (acc, key) => {
          // Remove 'custom-key-' prefix for the request param key
          const paramKey = key?.replace('custom-key-', '');
          acc[paramKey] = widgetData[key];
          return acc;
        },
        {} as Record<string, any>
      );

    const updatedData = {
      firstName: firstName,
      lastName: lastName,
      phone: phone,
      email: email,
      leadSourceId: leadSourceId,
      birthDate: '1991-01-01',
      country: tradein_address_country,
      zipCode: widgetData?.zipCode || tradein_address_postal,
      originalZipCode: tradein_address_postal,
      streetName: tradein_address_lookupStreetName,
      addressNumber: tradein_address_lookupStreetNumber,
      city: tradein_address_city,
      leadDetails: {
        Odometer: mileage,
        'Car Year': year,
        'Car Model': model,
        'Car Make': make,
        'Car Trim': trim,
        UVC: uvc || vehicleSearch?.uvc,
        'Vehicle Status': 'Used',
        'Offer Value': offerValue?.Value || offerValue?.ResponseMessage || 'Unavailable',
        'Widget Name': authenticateClientData?.data?.widgetName,
        'Widget ID': authenticateClientData?.data?.widgetId,
        organizationUniqueId: widgetData?.dealershipId,
      },
      province: province,
      adfDistributionList: widgetData?.adfDistributionList,
      ...customFields, // Spread custom fields here
    };

    if (version === '1.1') {
      const multiChoice = transformMultiChoiceData(vehicleOptionsArray, getValues(), t);

      const singleChoice = transformSingleChoiceData(transformQuestions(BLACK_BOOK_QUESTIONS_V2()), getValues(), t);

      const customChoice = transformSingleChoiceCustomData(VEHICLE_CONDITIONS_PAGE_2(t), getValues());

      if (multiChoice.length || singleChoice.length) {
        updatedData.leadDetails = {
          ...updatedData.leadDetails,
          questions: [...singleChoice, ...customChoice],
          interiorColor,
          exteriorColor,
          vehicleOptions: [...(multiChoice[0]?.answer?.length > 0 ? multiChoice : [])],
        };
      }
    }

    dispatch(
      postPublicLeadCreationOld({
        data: updatedData,
        leadTypeID: authenticateClientData?.data?.widgetData?.leadTypeId,
      })
    )
      .then(async (res) => {
        Logger.custom('lead_created', {
          id: res?.payload?.data?.Lead?.leadId || '',
          client: authenticateClientData?.data?.client || '',
          widgetName: authenticateClientData?.data?.widgetName || '',
          module: 'Trade-In',
        });
        if ('error' in res && Object.keys(res.error).length && res.error.message) {
          // call another api for lead creation if this api failed
          dispatch(
            postTradeInLeadCreation({
              data: updatedData,
              leadTypeID: authenticateClientData?.data?.widgetData?.leadTypeId,
            })
          ).then((resp) => {
            if (Object.keys(resp?.payload)?.length && resp?.payload?.message) {
              onNext?.();
            }
          });
        } else if (Object.keys(res.payload).length && res.payload.message) {
          onNext?.();
        }
      })
      .catch((error) => {
        console.error('Error postPublicLeadCreationOld:', error.message);
        Logger.error('Error in postPublicLeadCreationOld:', {
          errorData: error?.message || error,
          client: authenticateClientData?.data?.client || '',
          widgetName: authenticateClientData?.data?.widgetName || '',
          module: 'Trade-In',
        });
      });
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <>
      <div className='car-details'>
        <div className='car-details__heading'>
          <p>
            <strong>{headingWatcher}</strong>
          </p>
          <p>{t('stepperFormTradeIn.valueEstimate')}</p>
        </div>
        <ul className='car-details__usp list-unstyled mb-0'>
          <li>
            <div className='car-details__usp__icon'>
              <TaskAltIcon />
            </div>
            <p>{t('stepperFormTradeIn.note1')}</p>
          </li>
          <li>
            <div className='car-details__usp__icon'>
              <ForumIcon />
            </div>
            <p>{t('stepperFormTradeIn.note2')}</p>
          </li>
          <li>
            <div className='car-details__usp__icon'>
              <LocalOfferOutlinedIcon />
            </div>
            <p>{t('stepperFormTradeIn.note3')}</p>
          </li>
        </ul>
      </div>
      <div className='two-col'>
        <div className='form-group'>
          <CommonInput
            name='firstName'
            control={control}
            label={t('stepperForm.firstName')}
            placeholder={t('stepperForm.enterFirstName')}
            rules={formRequiredRules(errorMessage(t).firstNameRequired)}
            triggerValidationForField={trigger}
          />
        </div>
        <div className='form-group'>
          <CommonInput
            name='lastName'
            control={control}
            label={t('stepperForm.lastName')}
            placeholder={t('stepperForm.enterLastName')}
            rules={formRequiredRules(errorMessage(t).lastNameRequired)}
            triggerValidationForField={trigger}
          />
        </div>
        <div className='form-group'>
          <CommonPhoneInput
            name='phone'
            control={control}
            label={t('stepperForm.mobileNumber')}
            placeholder={t('stepperForm.enterNumber')}
            rules={{
              ...formRequiredRules(errorMessage(t).phoneRequired),
              ...formPatternRules(PHONE_REGEX, errorMessage(t).invalidPhoneFormat),
            }}
            triggerValidationForField={trigger}
          />
        </div>
        <div className='form-group'>
          <CommonInput
            name='email'
            control={control}
            hint={t('stepperForm.emailNote')}
            label={t('stepperForm.emailEg')}
            placeholder={t('stepperForm.enterEmail')}
            rules={{
              ...formRequiredRules(errorMessage(t).emailRequired),
              ...formPatternRules(EMAIL_REGEX, errorMessage(t).invalidEmailFormat),
            }}
            triggerValidationForField={trigger}
          />
        </div>
      </div>

      <div className='form-group'>
        <AutoCompleteAddress
          name='tradein_address'
          control={control}
          setValue={setValue}
          label={t('stepperForm.address')}
          placeholder={t('stepperForm.enterAddress')}
          rules={formRequiredRules(errorMessage(t).addressRequired)}
          triggerValidationForField={trigger}
        />
      </div>

      <div className='form-group'>
        <CheckboxInput
          name='termsAndCondition'
          control={control}
          label={t('stepperFormTradeIn.checkBoxNote', { dealershipName })}
          rules={formRequiredRules(errorMessage(t).termsAndConditionRequired)}
          triggerValidationForField={trigger}
        />
      </div>
    </>
  );
};

const PersonalInfoStep = React.forwardRef(PersonalInfo);

export default PersonalInfoStep;
